import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';
import { 
  ArrowLeft, DollarSign, TrendingUp, TrendingDown, Users, Briefcase, 
  Star, CreditCard, Calendar, RefreshCw, BarChart3, PieChart, Zap
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rangeType, setRangeType] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [topJobs, setTopJobs] = useState([]);
  const [topProfiles, setTopProfiles] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    try {
      let rangeParams = `range_type=${rangeType}`;
      if (rangeType === 'custom' && customStart && customEnd) {
        rangeParams += `&start_date=${customStart}T00:00:00Z&end_date=${customEnd}T23:59:59Z`;
      }
      
      const [summaryRes, comparisonRes, subsRes, convRes, jobsRes, profilesRes] = await Promise.all([
        api.get(`/analytics/revenue/summary?${rangeParams}`),
        api.get('/analytics/revenue/comparison'),
        api.get('/analytics/subscriptions/summary'),
        api.get(`/analytics/conversions?${rangeParams}`),
        api.get(`/analytics/top-performers/jobs?${rangeParams}`),
        api.get(`/analytics/top-performers/profiles?${rangeParams}`)
      ]);

      setSummary(summaryRes.data);
      setComparison(comparisonRes.data);
      setSubscriptions(subsRes.data);
      setConversions(convRes.data);
      setTopJobs(jobsRes.data.top_jobs || []);
      setTopProfiles(profilesRes.data.top_profiles || []);
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [rangeType, customStart, customEnd]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatPercent = (value) => {
    if (value > 0) return <span className="text-green-600">+{value}%</span>;
    if (value < 0) return <span className="text-red-600">{value}%</span>;
    return <span className="text-slate-500">0%</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Monetization Analytics</h1>
                <p className="text-sm text-slate-500">Track revenue across all monetization features</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={rangeType} onValueChange={setRangeType}>
                <SelectTrigger className="w-40" data-testid="range-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                  <SelectItem value="all_time">All Time</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {rangeType === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-36"
                    data-testid="custom-start-date"
                  />
                  <span className="text-slate-400">to</span>
                  <Input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-36"
                    data-testid="custom-end-date"
                  />
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={handleRefresh}
                    disabled={!customStart || !customEnd}
                    data-testid="apply-custom-range"
                  >
                    Apply
                  </Button>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Revenue Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <DollarSign className="w-10 h-10 opacity-80" />
                <div className="text-right">
                  <p className="text-3xl font-bold">{formatCurrency(summary?.total_revenue || 0)}</p>
                  <p className="text-emerald-100 text-sm">Total Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <CreditCard className="w-10 h-10 text-blue-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-800">{summary?.total_transactions || 0}</p>
                  <p className="text-slate-500 text-sm">Transactions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-10 h-10 text-cyan-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-800">{formatCurrency(subscriptions?.monthly_recurring_revenue || 0)}</p>
                  <p className="text-slate-500 text-sm">Monthly Recurring</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Users className="w-10 h-10 text-purple-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-800">{subscriptions?.total_active || 0}</p>
                  <p className="text-slate-500 text-sm">Active Subscriptions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Growth Metrics */}
        {comparison && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Growth Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold">{formatPercent(comparison.growth.daily)}</p>
                  <p className="text-sm text-slate-500 mt-1">Daily Growth</p>
                  <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>Today: {formatCurrency(comparison.periods.today.total)}</span>
                    <span>Yesterday: {formatCurrency(comparison.periods.yesterday.total)}</span>
                  </div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold">{formatPercent(comparison.growth.weekly)}</p>
                  <p className="text-sm text-slate-500 mt-1">Weekly Growth</p>
                  <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>This Week: {formatCurrency(comparison.periods.this_week.total)}</span>
                    <span>Last Week: {formatCurrency(comparison.periods.last_week.total)}</span>
                  </div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold">{formatPercent(comparison.growth.monthly)}</p>
                  <p className="text-sm text-slate-500 mt-1">Monthly Growth</p>
                  <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>This Month: {formatCurrency(comparison.periods.this_month.total)}</span>
                    <span>Last Month: {formatCurrency(comparison.periods.last_month.total)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Revenue by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.categories && summary.categories.length > 0 ? (
                <div className="space-y-3">
                  {summary.categories.map((cat, idx) => (
                    <div key={cat.type} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ 
                        backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'][idx % 6] 
                      }} />
                      <div className="flex-grow">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{cat.label}</span>
                          <span className="text-slate-500">{formatCurrency(cat.amount)}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 mt-1">
                          <div 
                            className="h-2 rounded-full" 
                            style={{ 
                              width: `${cat.percentage}%`,
                              backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'][idx % 6]
                            }} 
                          />
                        </div>
                      </div>
                      <span className="text-sm text-slate-400 w-12 text-right">{cat.percentage}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <PieChart className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>No revenue data for this period</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Subscription Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptions && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{subscriptions.new_this_month}</p>
                      <p className="text-xs text-green-700">New This Month</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{subscriptions.churned_last_30_days}</p>
                      <p className="text-xs text-red-700">Churned (30d)</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">By Plan</p>
                    {subscriptions.by_plan?.length > 0 ? (
                      <div className="space-y-2">
                        {subscriptions.by_plan.map((plan) => (
                          <div key={plan.plan} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                            <Badge variant="outline" className="capitalize">{plan.plan}</Badge>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-slate-500">{plan.count} active</span>
                              <span className="font-medium">{formatCurrency(plan.mrr)}/mo</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No active subscriptions</p>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Annual Recurring Revenue</span>
                      <span className="text-xl font-bold text-slate-800">{formatCurrency(subscriptions.annual_recurring_revenue)}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Conversion Analytics */}
        {conversions && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Conversion Analytics
              </CardTitle>
              <CardDescription>Compare performance of featured vs non-featured content</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-8">
                {/* Featured Jobs */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Featured Jobs
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs text-amber-700 mb-1">Featured</p>
                      <p className="text-lg font-bold">{conversions.featured_jobs.count} jobs</p>
                      <p className="text-sm text-amber-600">{conversions.featured_jobs.avg_applications_per_job} apps/job</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Regular</p>
                      <p className="text-lg font-bold">{conversions.non_featured_jobs.count} jobs</p>
                      <p className="text-sm text-slate-500">{conversions.non_featured_jobs.avg_applications_per_job} apps/job</p>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-slate-100 rounded flex items-center justify-center">
                    <span className="text-sm">Feature Lift: </span>
                    <span className="ml-1 font-bold">{formatPercent(conversions.featured_lift)}</span>
                  </div>
                </div>

                {/* Highlighted Profiles */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Highlighted Profiles
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-purple-700 mb-1">Highlighted</p>
                      <p className="text-lg font-bold">{conversions.highlighted_profiles.count} profiles</p>
                      <p className="text-sm text-purple-600">{conversions.highlighted_profiles.avg_bookings_per_profile} bookings/profile</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Regular</p>
                      <p className="text-lg font-bold">{conversions.non_highlighted_profiles.count} profiles</p>
                      <p className="text-sm text-slate-500">{conversions.non_highlighted_profiles.avg_bookings_per_profile} bookings/profile</p>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-slate-100 rounded flex items-center justify-center">
                    <span className="text-sm">Highlight Lift: </span>
                    <span className="ml-1 font-bold">{formatPercent(conversions.highlight_lift)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Performers */}
        <Tabs defaultValue="jobs">
          <TabsList className="mb-4">
            <TabsTrigger value="jobs">Top Featured Jobs</TabsTrigger>
            <TabsTrigger value="profiles">Top Highlighted Profiles</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Performing Featured Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                {topJobs.length > 0 ? (
                  <div className="space-y-2">
                    {topJobs.map((job, idx) => (
                      <div key={job.shift_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-sm font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <div className="flex-grow">
                          <p className="font-medium">{job.venue_name}</p>
                          <p className="text-xs text-slate-500">{formatDateMMDDYYYY(job.shift_date)}</p>
                        </div>
                        <Badge variant="outline">{job.boost_type}</Badge>
                        <div className="text-right">
                          <p className="font-medium">{job.applications} applications</p>
                          <p className="text-xs text-green-600">{job.conversion_rate}% conversion</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>No featured jobs data for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profiles">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Performing Highlighted Profiles</CardTitle>
              </CardHeader>
              <CardContent>
                {topProfiles.length > 0 ? (
                  <div className="space-y-2">
                    {topProfiles.map((profile, idx) => (
                      <div key={profile.user_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-sm font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <div className="flex-grow">
                          <p className="font-medium">{profile.name}</p>
                          <p className="text-xs text-slate-500">Paid: {formatCurrency(profile.highlight_amount)}</p>
                        </div>
                        {profile.is_highlighted && (
                          <Badge className="bg-purple-500">Active</Badge>
                        )}
                        <div className="text-right">
                          <p className="font-medium">{profile.total_bookings} bookings</p>
                          <p className="text-xs text-green-600">{profile.confirmed_bookings} confirmed</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Star className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>No highlighted profiles data for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
